import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  useToast,
  Alert,
  AlertIcon,
  FormHelperText,
  FormErrorMessage,
} from '@chakra-ui/react';
import { submitContactMessage } from '../services/nihTerminationService';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const toast = useToast();
  
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      errors.email = 'Invalid email address';
    }
    
    if (!formData.subject.trim()) {
      errors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: 'Form Error',
        description: 'Please check the form for errors.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await submitContactMessage({
        ...formData,
      });
      
      if (result.success) {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
        });
        
        toast({
          title: "Message sent!",
          description: "We've received your message and will get back to you soon.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } else {
        throw new Error(result.error || 'Failed to send message');
      }
    } catch (error) {
      toast({
        title: "Error sending message",
        description: error.message || "There was an error sending your message. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxW="800px" py={10}>
      <VStack spacing={8} align="stretch">
        <Box>
          <Heading as="h1" size="xl" mb={4}>
            Contact Us
          </Heading>
          <Text fontSize="lg">
            Have questions or feedback about the NIH Termination Tracker? We'd love to hear from you.
          </Text>
        </Box>

        {submitted ? (
          <Alert status="success" borderRadius="md" mb={4}>
            <AlertIcon />
            Your message has been sent successfully. We'll get back to you soon.
          </Alert>
        ) : (
          <Box as="form" onSubmit={handleSubmit} bg="white" p={8} borderRadius="md" boxShadow="md">
            <VStack spacing={5} align="stretch">
              <FormControl isRequired isInvalid={!!formErrors.name}>
                <FormLabel>Your Name</FormLabel>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Full Name"
                />
                <FormErrorMessage>{formErrors.name}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={!!formErrors.email}>
                <FormLabel>Email Address</FormLabel>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@institution.edu"
                />
                <FormHelperText>We'll never share your email with anyone else.</FormHelperText>
                <FormErrorMessage>{formErrors.email}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={!!formErrors.subject}>
                <FormLabel>Subject</FormLabel>
                <Input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Subject of your message"
                />
                <FormErrorMessage>{formErrors.subject}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={!!formErrors.message}>
                <FormLabel>Message</FormLabel>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Your message here..."
                  rows={6}
                />
                <FormErrorMessage>{formErrors.message}</FormErrorMessage>
              </FormControl>

              <Button
                type="submit"
                colorScheme="blue"
                isLoading={isSubmitting}
                loadingText="Sending"
                size="lg"
                width="full"
              >
                Send Message
              </Button>
            </VStack>
          </Box>
        )}

        <Box>
          <Heading as="h2" size="md" mb={3}>
            Alternative Contact Methods
          </Heading>
          <Text>
            If you prefer, you can also reach us directly at:
          </Text>
          <Text fontWeight="bold" mt={2}>
            Email: hants.williams@stonybrook.edu
          </Text>
        </Box>
      </VStack>
    </Container>
  );
};

export default Contact;